import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ProjectView from "../../components/homeowner/ProjectView";
import {
  HomeownerApiError,
  homeownerFile,
  homeownerRequest,
} from "../../lib/homeownerApi";
import type {
  HomeownerProjectData,
  HomeownerPublication,
  HomeownerRequest,
} from "../../types/homeowner";
export default function HomeownerPage() {
  const { token = "" } = useParams<{ token: string }>();
  const storageKey = `homeowner-session:${token}`;
  const [session, setSession] = useState<string | null>(() =>
    sessionStorage.getItem(storageKey),
  );
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("question");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const [saved, setSaved] = useState("");
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ["homeowner", token, session],
    enabled: !!session,
    queryFn: () => homeownerRequest<HomeownerProjectData>(token, session),
    retry: false,
  });
  const logout = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    setSession(null);
    cache.removeQueries({ queryKey: ["homeowner", token] });
  }, [storageKey, cache, token]);
  useEffect(() => {
    if (
      query.error instanceof HomeownerApiError &&
      [401, 403, 404].includes(query.error.status)
    )
      logout();
  }, [query.error, logout]);
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
  const verify = useMutation({
    mutationFn: () =>
      homeownerRequest<{ session: string }>(token, null, "verify/", "POST", {
        email,
        code,
      }),
    onSuccess: (data) => {
      sessionStorage.setItem(storageKey, data.session);
      setSession(data.session);
      setCode("");
    },
  });
  const send = useMutation({
    mutationFn: () =>
      homeownerRequest<HomeownerRequest>(token, session, "requests/", "POST", {
        request_key: requestKey,
        kind,
        subject,
        message,
      }),
    onSuccess: () => {
      setSubject("");
      setMessage("");
      setRequestKey(crypto.randomUUID());
      setSaved("Your request is saved. Your contractor can now follow up.");
      void query.refetch();
    },
  });
  const loadFile = useCallback(
    (item: HomeownerPublication) =>
      homeownerFile(token, session || "", item.file_url || ""),
    [token, session],
  );
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f3f4f8", p: { xs: 1, md: 4 } }}>
      {!session ? (
        <Card sx={{ maxWidth: 460, mx: "auto", mt: { xs: 5, md: 10 }, p: 3 }}>
          <Typography level="h1" sx={{ fontSize: "2rem" }}>
            Your private project hub
          </Typography>
          <Typography>
            Enter the email address and separate access code provided by your
            contractor.
          </Typography>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              verify.mutate();
            }}
          >
            <Stack spacing={2} sx={{ mt: 2 }}>
              <FormControl>
                <FormLabel>Email address</FormLabel>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  slotProps={{
                    input: {
                      "aria-label": "Homeowner email",
                      autoComplete: "email",
                    },
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Project access code</FormLabel>
                <Input
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  slotProps={{
                    input: {
                      "aria-label": "Project access code",
                      autoComplete: "off",
                    },
                  }}
                />
              </FormControl>
              {verify.error && (
                <Alert color="danger">{verify.error.message}</Alert>
              )}
              <Button type="submit" loading={verify.isPending}>
                Open my project
              </Button>
            </Stack>
          </form>
          <Typography level="body-sm">
            If you need a code or a new invitation, contact your contractor. A
            project link alone does not grant access.
          </Typography>
        </Card>
      ) : (
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
              variant="plain"
              onClick={async () => {
                try {
                  await homeownerRequest(token, session, "", "DELETE");
                } finally {
                  logout();
                }
              }}
            >
              Sign out of project
            </Button>
          </Box>
          {query.isLoading && <Typography>Loading your project…</Typography>}
          {query.error && <Alert color="danger">{query.error.message}</Alert>}
          {query.data && (
            <ProjectView data={query.data} loadFile={loadFile}>
              <Box>
                <Typography level="h2" sx={{ fontSize: "1.5rem", mb: 2 }}>
                  Questions & service
                </Typography>
                <Card variant="outlined" sx={{ boxShadow: "none" }}>
                  <Typography>
                    Send a question or request a service visit. For an urgent
                    leak, call your contractor directly.
                  </Typography>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setSaved("");
                      send.mutate();
                    }}
                  >
                    <Stack spacing={2}>
                      <Select
                        aria-label="Request type"
                        value={kind}
                        onChange={(_, v) => setKind(v || "question")}
                      >
                        <Option value="question">Project question</Option>
                        <Option value="service">Service request</Option>
                      </Select>
                      <Input
                        placeholder="Subject"
                        required
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        slotProps={{
                          input: {
                            "aria-label": "Request subject",
                            maxLength: 255,
                            minLength: 3,
                          },
                        }}
                      />
                      <Textarea
                        placeholder="How can we help?"
                        required
                        minRows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        slotProps={{
                          textarea: {
                            "aria-label": "Request message",
                            maxLength: 4000,
                            minLength: 8,
                          },
                        }}
                      />
                      <Button type="submit" loading={send.isPending}>
                        Send request
                      </Button>
                      {send.error && (
                        <Alert color="danger">{send.error.message}</Alert>
                      )}
                      {saved && <Alert color="success">{saved}</Alert>}
                    </Stack>
                  </form>
                </Card>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {query.data.requests.map((row) => (
                    <Card
                      key={row.id}
                      variant="outlined"
                      sx={{ boxShadow: "none" }}
                    >
                      <Typography level="title-md">{row.subject}</Typography>
                      <Typography level="body-xs">
                        {new Date(row.created_at).toLocaleString()} ·{" "}
                        {row.status.replaceAll("_", " ")}
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-wrap" }}>
                        {row.message}
                      </Typography>
                      {row.response && (
                        <Alert color="success">
                          <Box>
                            <Typography level="title-sm">
                              {row.responded_by}
                            </Typography>
                            <Typography sx={{ whiteSpace: "pre-wrap" }}>
                              {row.response}
                            </Typography>
                          </Box>
                        </Alert>
                      )}
                    </Card>
                  ))}
                </Stack>
              </Box>
            </ProjectView>
          )}
        </Box>
      )}
    </Box>
  );
}
